// When the `action_ingredient_add` event is recorded, and why it is decided here.
//
// The event used to be emitted straight after the portion screen was PUSHED, which
// is not the same thing as the user adding anything: `pushAsync` resolves as soon as
// the page is on screen, and it also returns early without showing a page at all (a
// pending reload, the 500 ms duplicate-push suppressor, a page filter redirect). So
// somebody who tapped a food, looked at the portion screen and backed out was counted
// as having added it.
//
// That matters beyond a miscounted event. `action_ingredient_add` is one of the
// FOOD_LOGGED_EVENTS in `services/telemetry-profile-props.ts`, and the first sighting
// of any of those flips the sticky `settings.hasLoggedFoodEver` marker and the
// `has_logged_food_ever` Refiner trait. The marker is written once per user and never
// cleared, so an abandoned look recorded somebody for ever as having logged food, and
// dropped them out of the "never logged food" survey segment that trait exists for.
//
// The decision lives in this module, away from the component, because the component
// has no test harness — the same reason `cs-day-items-load-recovery.ts` and
// `telemetry-profile-props.ts` next door are plain modules.

import { FoodStatus } from '@cf-shared/data-model/food';
import { EditIngredientResult } from 'pages/edit-ingredient/edit-ingredient-interface';
import { foodIdForTelemetry } from './ingredient-remove-telemetry';

/**
 * The food a portion screen was opened for, carried through the screen's return
 * context so it comes back with the result.
 *
 * It travels in the nav parameters rather than in a field on the component on purpose.
 * The food list stays open while the portion screen sits on top of it (#4967), so one
 * of these can follow another in quick succession; and the return context survives a
 * page reload, where an in-memory field would not.
 */
export interface IngredientAddTelemetryFood {
  foodId: string;
  status?: FoodStatus;
}

/** What comes back as the first argument of `handleEditResult`. */
export interface IngredientEditReturnContext {
  index: number;
  /** Set only when the portion screen was opened by picking a food from the food list. */
  addedFood?: IngredientAddTelemetryFood;
  /**
   * Set only when the portion screen was opened to confirm a whole past meal: View All, the
   * other rows of the recent-meals card, or a past meal picked in the food list (#6237).
   */
  reusedPastMeal?: boolean;
}

/**
 * Which `action_ingredient_add` events this portion-screen result should record: one per entry,
 * none for an empty list.
 *
 * Two routes record, and every other route into the portion screen (a custom food, editing
 * something already in the list) records nothing HERE:
 *
 *  - **a food picked from the food list** records ONE add, for the food that was picked, when
 *    the result actually added it. That is a save of a new ingredient, or a non-empty split —
 *    picking a recipe and choosing "Split into foods" really does add food, and the old
 *    emit-on-open covered it, so leaving it out would swap an overcount for an undercount.
 *  - **a past meal confirmed on the meal screen** records one add PER FOOD it handed back, each
 *    as a recent food. It used to record nothing at all, so a user who logged food only this way
 *    never flipped the marker (#6237). Per food and `Recent` because that is what the one-tap
 *    "Add Again" on the same card records from `ingredient-list.ts` itself (#4969): the two do
 *    the same thing, one with a confirm step and one without, so they should read the same in
 *    the numbers. The day route's past-meal Log is different — it records one `foods_log_food`
 *    per reuse from the portion screen, which this function never sees.
 *
 * A save carries `index === -1` for a new ingredient; anything else is an edit of a row that is
 * already in the list.
 */
export function ingredientAddsToTrack(
  context: IngredientEditReturnContext | undefined,
  result: EditIngredientResult | undefined,
): IngredientAddTelemetryFood[] {
  if (!context || !result) return [];

  if (context.addedFood) {
    return resultAddedSomething(context, result) ? [context.addedFood] : [];
  }
  if (context.reusedPastMeal && result.action === 'split') {
    return (result.splitIngredients ?? []).map(ingredient => ({
      foodId: foodIdForTelemetry(ingredient),
      status: FoodStatus.Recent,
    }));
  }
  return [];
}

/** whether a food-list pick came back as something that went into the list */
function resultAddedSomething(context: IngredientEditReturnContext, result: EditIngredientResult): boolean {
  if (result.action === 'save') {
    return !!result.ingredient && context.index === -1;
  }
  if (result.action === 'split') {
    return !!result.splitIngredients?.length;
  }
  return false;
}
